import { useReducer, useMemo, useCallback, useState } from 'react'
import { COLUMN_CATALOG_MAP, DEFAULT_CONFIG, GROUPING_PRESETS, createColumnInstance, regenerateColumnIds } from '../lib/constants'
import { migrateConfig, CURRENT_CONFIG_VERSION } from '../lib/migrateConfig'
import { resolveComparisonColumnRefs } from '../lib/comparisonColumns'
import { resolveWindowColumnRefs } from '../lib/windowColumns'
import { resolveColumnHeader } from '../lib/resolveColumnHeader'

const actions = {
  SET_GROUPINGS: 'SET_GROUPINGS',
  ADD_GROUPING: 'ADD_GROUPING',
  REMOVE_GROUPING: 'REMOVE_GROUPING',
  REORDER_GROUPINGS: 'REORDER_GROUPINGS',
  ADD_COLUMN: 'ADD_COLUMN',
  REMOVE_COLUMN: 'REMOVE_COLUMN',
  SET_COLUMNS: 'SET_COLUMNS',
  SET_QUICK_DATE: 'SET_QUICK_DATE',
  SET_DATE_RANGE: 'SET_DATE_RANGE',
  SET_FETCH_ALL_LEVELS: 'SET_FETCH_ALL_LEVELS',
  SET_INCLUDE_BENCHMARKS: 'SET_INCLUDE_BENCHMARKS',
  SET_GROUPING_PRESET: 'SET_GROUPING_PRESET',
  LOAD_CONFIG: 'LOAD_CONFIG',
  RESET: 'RESET'
}

function reducer (state, action) {
  switch (action.type) {
    case actions.SET_GROUPINGS:
      return { ...state, groupings: action.payload, groupingPreset: 'custom' }
    case actions.ADD_GROUPING:
      if (state.groupings.includes(action.payload)) return state
      return { ...state, groupings: [...state.groupings, action.payload], groupingPreset: 'custom' }
    case actions.REMOVE_GROUPING:
      return { ...state, groupings: state.groupings.filter(g => g !== action.payload), groupingPreset: 'custom' }
    case actions.REORDER_GROUPINGS:
      return { ...state, groupings: action.payload, groupingPreset: 'custom' }
    case actions.SET_GROUPING_PRESET: {
      const preset = GROUPING_PRESETS.find(p => p.value === action.payload)
      if (!preset || preset.value === 'custom') {
        return { ...state, groupingPreset: 'custom' }
      }
      return { ...state, groupingPreset: preset.value, groupings: preset.groupings }
    }
    case actions.ADD_COLUMN: {
      const instance = action.payload
      if (!instance) return state
      return { ...state, columns: [...state.columns, instance] }
    }
    case actions.REMOVE_COLUMN:
      return { ...state, columns: state.columns.filter(c => c.id !== action.payload) }
    case actions.SET_COLUMNS:
      return { ...state, columns: action.payload }
    case actions.SET_QUICK_DATE:
      return { ...state, dateMode: 'quickDate', quickDate: action.payload, startDate: null, endDate: null }
    case actions.SET_DATE_RANGE:
      return { ...state, dateMode: 'dateRange', quickDate: null, startDate: action.payload.startDate, endDate: action.payload.endDate }
    case actions.SET_FETCH_ALL_LEVELS:
      return { ...state, fetchAllLevels: action.payload }
    case actions.SET_INCLUDE_BENCHMARKS:
      return { ...state, includeBenchmarks: action.payload }
    case actions.LOAD_CONFIG: {
      const migrated = migrateConfig(action.payload.configuration, action.payload.configVersion)
      const regenerated = regenerateColumnIds(migrated.columns || [])
      const columns = resolveComparisonColumnRefs(
        resolveWindowColumnRefs(regenerated, COLUMN_CATALOG_MAP),
        COLUMN_CATALOG_MAP
      )
      return { ...migrated, columns }
    }
    case actions.RESET:
      return { ...DEFAULT_CONFIG }
    default:
      return state
  }
}

export function useExplorerConfig () {
  const [config, dispatch] = useReducer(reducer, DEFAULT_CONFIG)

  const columnDefinitions = useMemo(() => {
    return config.columns.map(col => {
      const catalog = COLUMN_CATALOG_MAP[col.columnType]
      if (!catalog) return null

      const header = resolveColumnHeader(col, catalog, config.columns)

      let format = catalog.format
      let measureKind = catalog.measureKind
      if (catalog.kind === 'comparison') {
        const op = col.options?.operation || 'difference'
        if (op === 'ratio' || op === 'percentChange') {
          format = 'returns'
          measureKind = 'ratio'
        } else {
          const instA = col.options?.columnAId ? config.columns.find(c => c.id === col.options.columnAId) : null
          const catA = instA ? COLUMN_CATALOG_MAP[instA.columnType] : null
          format = catA?.format || 'marketValue'
          measureKind = catA?.measureKind || 'currency'
        }
      }

      return {
        accessorKey: col.id,
        header,
        format,
        measureKind,
        align: catalog.align
      }
    }).filter(Boolean)
  }, [config.columns])

  const [cleanSnapshot, setCleanSnapshot] = useState(null)

  const loadConfig = useCallback((savedConfig) => {
    dispatch({ type: actions.LOAD_CONFIG, payload: savedConfig })
    setCleanSnapshot(JSON.stringify({
      configuration: savedConfig.configuration,
      configVersion: savedConfig.configVersion ?? CURRENT_CONFIG_VERSION
    }))
  }, [])

  const serializeConfig = useCallback(() => ({
    configuration: {
      ...config,
      columns: config.columns.map(({ columnType, label, options }) => {
        const catalog = COLUMN_CATALOG_MAP[columnType]
        if (catalog?.kind === 'window' && options?.sourceColumnId) {
          const sourceIndex = config.columns.findIndex(c => c.id === options.sourceColumnId)
          const { sourceColumnId, ...rest } = options
          return {
            columnType,
            label,
            options: { ...rest, sourceColumnIndex: sourceIndex >= 0 ? sourceIndex : undefined }
          }
        }
        if (catalog?.kind === 'comparison') {
          const { columnAId, columnBId, ...rest } = options || {}
          const columnAIndex = columnAId ? config.columns.findIndex(c => c.id === columnAId) : -1
          const columnBIndex = columnBId ? config.columns.findIndex(c => c.id === columnBId) : -1
          return {
            columnType,
            label,
            options: {
              ...rest,
              columnAIndex: columnAIndex >= 0 ? columnAIndex : undefined,
              columnBIndex: columnBIndex >= 0 ? columnBIndex : undefined
            }
          }
        }
        return { columnType, label, options }
      })
    },
    configVersion: CURRENT_CONFIG_VERSION
  }), [config])

  const isDirty = useMemo(() => {
    if (!cleanSnapshot) return false
    return JSON.stringify(serializeConfig()) !== cleanSnapshot
  }, [serializeConfig, cleanSnapshot])

  const markClean = useCallback(() => {
    setCleanSnapshot(JSON.stringify(serializeConfig()))
  }, [serializeConfig])

  return {
    config,
    columnDefinitions,
    setGroupings: (groupings) => dispatch({ type: actions.SET_GROUPINGS, payload: groupings }),
    addGrouping: (grouping) => dispatch({ type: actions.ADD_GROUPING, payload: grouping }),
    removeGrouping: (grouping) => dispatch({ type: actions.REMOVE_GROUPING, payload: grouping }),
    reorderGroupings: (groupings) => dispatch({ type: actions.REORDER_GROUPINGS, payload: groupings }),
    setGroupingPreset: (preset) => dispatch({ type: actions.SET_GROUPING_PRESET, payload: preset }),
    addColumn: (columnType, optionOverrides) => dispatch({ type: actions.ADD_COLUMN, payload: createColumnInstance(columnType, optionOverrides) }),
    removeColumn: (id) => dispatch({ type: actions.REMOVE_COLUMN, payload: id }),
    setColumns: (columns) => dispatch({ type: actions.SET_COLUMNS, payload: columns }),
    setQuickDate: (quickDate) => dispatch({ type: actions.SET_QUICK_DATE, payload: quickDate }),
    setDateRange: (startDate, endDate) => dispatch({ type: actions.SET_DATE_RANGE, payload: { startDate, endDate } }),
    setFetchAllLevels: (value) => dispatch({ type: actions.SET_FETCH_ALL_LEVELS, payload: value }),
    setIncludeBenchmarks: (value) => dispatch({ type: actions.SET_INCLUDE_BENCHMARKS, payload: value }),
    loadConfig,
    serializeConfig,
    isDirty,
    markClean,
    reset: () => dispatch({ type: actions.RESET })
  }
}
